import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import styled from "styled-components";

import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

// Register Chart.js modules
ChartJS.register(CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement, Filler);

const CardWrapper = styled.div`
  width: 100%;
  height: 100%;
  /* No position: relative here */
`;

const ChartWrapper = styled.div`
  position: relative; /* <-- This is important */
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;



const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const QuarterlySalesComparison = ({ data }) => {
  const [salesData, setSalesData] = useState({});
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  const calculateQuarterlySales = (row) =>
    parseFloat(row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22) || 0;

  const getQuarter = (month) => {
    if (month >= 1 && month <= 3) return "Q1";
    if (month >= 4 && month <= 6) return "Q2";
    if (month >= 7 && month <= 9) return "Q3";
    if (month >= 10 && month <= 12) return "Q4";
    return null;
  };

  useEffect(() => {
    if (data && data.length > 0) {
      const groupedData = data.reduce((acc, row) => {
        const year = parseInt(row.Rok, 10);
        const month = parseInt(row.Miesiac, 10);
        const quarter = getQuarter(month);
        const sales = calculateQuarterlySales(row);

        if (!acc[year]) acc[year] = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
        if (quarter) acc[year][quarter] += sales;

        return acc;
      }, {});

      const years = Object.keys(groupedData).map((y) => parseInt(y, 10));
      const latestYear = Math.max(...years);

      setSalesData(groupedData);
      setAvailableYears(years.sort((a, b) => b - a));
      setSelectedYear(latestYear);
    }
  }, [data]);

useEffect(() => {
  if (selectedYear && salesData) {
    const quarters = ["Q1", "Q2", "Q3", "Q4"];
    const currentYearData = quarters.map((q) => salesData[selectedYear]?.[q] || 0);
    const previousYearData = quarters.map((q) => salesData[selectedYear - 1]?.[q] || 0);

    setChartData({
      labels: quarters,
      datasets: [
        {
          label: `Sprzedaż ${selectedYear} (PLN)`,
          data: currentYearData,
          borderColor: "#2e7d32",               // current year = green
          backgroundColor: "rgba(46,125,50,0.2)",
          fill: true,
          pointBackgroundColor: "#2e7d32",
          tension: 0.4,
        },
        {
          label: `Sprzedaż ${selectedYear - 1} (PLN)`,
          data: previousYearData,
          borderColor: "#fb8c00",               // previous year = orange
          backgroundColor: "rgba(251,140,0,0.2)",
          fill: true,
          pointBackgroundColor: "#fb8c00",
          tension: 0.4,
        },
      ],
    });
  }
}, [selectedYear, salesData]);


  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!chartData) return <p>Ładowanie danych...</p>;

return (
  <CardWrapper>
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="quarterly-sales-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="quarterly-sales-tooltip"
        place="left"
        content={
          <>
            <strong>Porównanie kwartalnej sprzedaży</strong>
            <br />
            Ten wykres przedstawia wartość sprzedaży<br />
            z podziałem na kwartały ({selectedYear} vs {selectedYear - 1}).
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <Line
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Sprzedaż",
              },
              ticks: {
                callback: function (value) {
                  return Number(value)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                },
              },
            },
          },
          plugins: {
            title: {
              display: true,
              text: `Sprzedaż kwartalna: ${selectedYear} vs ${selectedYear - 1}`,
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  const label = context.dataset.label || "";
                  const value = context.parsed.y || 0;
                  const formattedValue = Number(value)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                  return `${label}: ${formattedValue}`;
                },
              },
            },
          },
        }}
      />
    </ChartWrapper>
  </CardWrapper>
);



};

export default QuarterlySalesComparison;
